/**
 * SoleadLab 2.0 — Photo Registration Wizard
 *
 * 4-step floating panel for correcting drone photo GPS positions.
 * Supports 3 tiers: analytical (gimbal EXIF), user-guided, anchor-based.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useProjectStore, useMapStore, useUIStore } from '../../stores';
import { Button } from '../common';
import {
  groupPhotosByFlight,
  getPhotosForFlight,
  enrichPhotosWithExif,
  batchAnalyticalCorrection,
  batchManualCorrection,
  batchAnchorCorrection,
  rankAnchorCandidates,
  applyRegistrationResults,
  computeResultStats,
  compassLabel,
  computeFlightHeading,
  type FlightGroup,
  type PhotoInput,
  type RegistrationResult,
  type RegistrationAnchor,
} from '../../services/photoRegistration';
import PhotoAnchorView from './PhotoAnchorView';
import './PhotoRegistrationWizard.css';

type WizardStep = 'flight' | 'configure' | 'preview' | 'done';
type Tier = 'gimbal' | 'manual' | 'anchor';

const STEPS: { key: WizardStep; label: string }[] = [
  { key: 'flight', label: 'Flight' },
  { key: 'configure', label: 'Configure' },
  { key: 'preview', label: 'Preview' },
  { key: 'done', label: 'Apply' },
];

interface Props {
  onClose: () => void;
}

export default function PhotoRegistrationWizard({ onClose }: Props) {
  const indications = useProjectStore((s) => s.data.indications);
  const geoPhotos = useProjectStore((s) => s.data.geoPhotos);
  const flightPaths = useProjectStore((s) => s.data.flightPaths);
  const addNotification = useUIStore((s) => s.addNotification);

  // Wizard state
  const [step, setStep] = useState<WizardStep>('flight');
  const [selectedFlight, setSelectedFlight] = useState<FlightGroup | null>(null);
  const [tier, setTier] = useState<Tier>('gimbal');
  const [photos, setPhotos] = useState<PhotoInput[]>([]);
  const [results, setResults] = useState<RegistrationResult[]>([]);
  const [enriching, setEnriching] = useState(false);

  // Tier 2 controls
  const [headingDeg, setHeadingDeg] = useState(0);
  const [tiltDeg, setTiltDeg] = useState(10);
  const [detectedHeading, setDetectedHeading] = useState<number | null>(null);

  // Tier 3 anchor
  const [showAnchorView, setShowAnchorView] = useState(false);

  // Flight groups
  const flights = useMemo(
    () => groupPhotosByFlight(indications, geoPhotos),
    [indications, geoPhotos],
  );

  // Auto-select single flight
  useEffect(() => {
    if (flights.length === 1 && !selectedFlight) {
      handleSelectFlight(flights[0]);
    }
  }, [flights]); // eslint-disable-line react-hooks/exhaustive-deps

  // Detect heading from flight paths
  useEffect(() => {
    if (flightPaths.length > 0) {
      const heading = computeFlightHeading(flightPaths[0].coordinates as [number, number, number][]);
      if (heading !== null) {
        setDetectedHeading(heading);
        setHeadingDeg(Math.round(heading));
      }
    }
  }, [flightPaths]);

  // ─── Handlers ──────────────────────────────────────

  const handleSelectFlight = useCallback(async (flight: FlightGroup) => {
    setSelectedFlight(flight);
    const photoInputs = getPhotosForFlight(flight, indications, geoPhotos);

    // Enrich photos with EXIF data from blob store
    setEnriching(true);
    const enriched = await enrichPhotosWithExif(photoInputs);
    setEnriching(false);
    if (enriched > 0) {
      // Re-count gimbal data
      flight.gimbalDataCount = photoInputs.filter(
        (p) => p.gimbalPitch != null && p.gimbalYaw != null
      ).length;
    }

    setPhotos(photoInputs);

    // Auto-detect tier
    if (flight.gimbalDataCount > 0) {
      setTier('gimbal');
    } else if (flightPaths.length > 0 || photoInputs.some((p) => p.bearing != null)) {
      setTier('manual');
    } else {
      setTier('manual'); // default to manual input
    }

    setStep('configure');
  }, [indications, geoPhotos, flightPaths]);

  const handleComputePreview = useCallback(() => {
    let res: RegistrationResult[];
    const avgAlt = photos.reduce((s, p) => s + (p.altitude ?? 50), 0) / photos.length;

    if (tier === 'gimbal') {
      res = batchAnalyticalCorrection(photos, 50);
      // For photos without gimbal data, fallback to manual with average values
      const withGimbal = new Set(res.map((r) => r.photoId));
      const remaining = photos.filter((p) => !withGimbal.has(p.id));
      if (remaining.length > 0) {
        // Compute average gimbal values from successful photos
        const gimbalPhotos = photos.filter((p) => p.gimbalPitch != null && p.gimbalYaw != null);
        if (gimbalPhotos.length > 0) {
          const avgPitch = gimbalPhotos.reduce((s, p) => s + p.gimbalPitch!, 0) / gimbalPhotos.length;
          const avgYaw = gimbalPhotos.reduce((s, p) => s + p.gimbalYaw!, 0) / gimbalPhotos.length;
          const tilt = 90 + avgPitch;
          const manualRes = batchManualCorrection(remaining, avgYaw, tilt, avgAlt);
          res = [...res, ...manualRes];
        }
      }
    } else if (tier === 'manual') {
      res = batchManualCorrection(photos, headingDeg, tiltDeg, avgAlt);
    } else {
      // Tier 3 anchor — should have been handled via anchor view already
      addNotification({ type: 'warning', title: 'Use "Place Anchors" button first' });
      return;
    }

    setResults(res);
    if (res.length > 0) {
      setStep('preview');
      // Show trail lines on map
      showTrailLines(res);
    } else {
      addNotification({ type: 'warning', title: 'No results', message: 'Could not compute corrections' });
    }
  }, [tier, photos, headingDeg, tiltDeg, addNotification]);

  const handleApply = useCallback(() => {
    applyRegistrationResults(results);
    const stats = computeResultStats(results);
    addNotification({
      type: 'success',
      title: `Registered ${stats.count} photos`,
      message: `Average offset: ${stats.avgOffset.toFixed(1)}m toward ${compassLabel(stats.avgDirection)}`,
    });
    setStep('done');
    // Clean up trail lines after a delay
    setTimeout(() => removeTrailLines(), 10000);
  }, [results, addNotification]);

  // Tier 3: handle anchors returned from PhotoAnchorView
  const handleAnchorsDone = useCallback((anchorResults: RegistrationAnchor[]) => {
    setShowAnchorView(false);
    if (anchorResults.length < 2) {
      addNotification({ type: 'warning', title: 'Need at least 2 anchors' });
      return;
    }
    const res = batchAnchorCorrection(photos, anchorResults);
    setResults(res);
    if (res.length > 0) {
      setStep('preview');
      showTrailLines(res);
    } else {
      addNotification({ type: 'warning', title: 'No results', message: 'Transform could not be computed' });
    }
  }, [photos, addNotification]);

  const handleAnchorCancel = useCallback(() => {
    setShowAnchorView(false);
  }, []);

  // Anchor candidates for Tier 3
  const anchorCandidates = useMemo(
    () => tier === 'anchor' ? rankAnchorCandidates(photos, null) : [],
    [tier, photos],
  );

  // ─── Map Trail Lines ───────────────────────────────

  const showTrailLines = (res: RegistrationResult[]) => {
    const map = useMapStore.getState().map;
    if (!map) return;

    const features = res
      .filter((r) => r.offsetMeters > 0.1)
      .map((r) => ({
        type: 'Feature' as const,
        properties: { name: r.photoName, offset: r.offsetMeters },
        geometry: {
          type: 'LineString' as const,
          coordinates: [r.oldPos, r.newPos],
        },
      }));

    if (features.length === 0) return;

    const sourceId = 'photo-reg-trails';
    const layerId = 'photo-reg-trails-line';
    try {
      if (map.getLayer(layerId)) map.removeLayer(layerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);
    } catch { /* ignore */ }

    map.addSource(sourceId, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features },
    });
    map.addLayer({
      id: layerId,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': '#f97316',
        'line-width': 2,
        'line-dasharray': [4, 2],
      },
    });
  };

  const removeTrailLines = () => {
    const map = useMapStore.getState().map;
    if (!map) return;
    try {
      if (map.getLayer('photo-reg-trails-line')) map.removeLayer('photo-reg-trails-line');
      if (map.getSource('photo-reg-trails')) map.removeSource('photo-reg-trails');
    } catch { /* ignore */ }
  };

  // ─── Computed Values ───────────────────────────────

  const stats = useMemo(() => computeResultStats(results), [results]);
  const stepIdx = STEPS.findIndex((s) => s.key === step);

  // Quick heading estimate for display
  const estimatedOffset = useMemo(() => {
    const avgAlt = photos.length > 0
      ? photos.reduce((s, p) => s + (p.altitude ?? 50), 0) / photos.length
      : 50;
    const rad = tiltDeg * Math.PI / 180;
    return (avgAlt * Math.tan(rad)).toFixed(1);
  }, [photos, tiltDeg]);

  // ─── Render ────────────────────────────────────────

  const content = (
    <div className="photo-reg-overlay" onClick={onClose}>
      <div className="photo-reg-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="photo-reg-header">
          <div className="photo-reg-header__left">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            <span className="photo-reg-title">Photo Registration</span>
          </div>
          <button className="photo-reg-close" onClick={onClose}>×</button>
        </div>

        {/* Step indicators */}
        <div className="photo-reg-steps">
          {STEPS.map((s, i) => (
            <div
              key={s.key}
              className={`photo-reg-step ${i === stepIdx ? 'photo-reg-step--active' : ''} ${i < stepIdx ? 'photo-reg-step--done' : ''}`}
            >
              <span className="photo-reg-step__num">{i < stepIdx ? '✓' : i + 1}</span>
              <span>{s.label}</span>
            </div>
          ))}
        </div>

        {/* Body */}
        <div className="photo-reg-body">
          {step === 'flight' && renderFlightStep()}
          {step === 'configure' && renderConfigureStep()}
          {step === 'preview' && renderPreviewStep()}
          {step === 'done' && renderDoneStep()}
        </div>

        {/* Footer */}
        <div className="photo-reg-footer">
          <div>
            {step !== 'flight' && step !== 'done' && (
              <Button variant="ghost" size="sm" onClick={() => {
                if (step === 'configure') { setStep('flight'); setSelectedFlight(null); }
                else if (step === 'preview') { setStep('configure'); removeTrailLines(); }
              }}>
                Back
              </Button>
            )}
          </div>
          <div className="photo-reg-footer__right">
            <Button variant="secondary" size="sm" onClick={() => { removeTrailLines(); onClose(); }}>
              {step === 'done' ? 'Close' : 'Cancel'}
            </Button>
            {step === 'configure' && tier !== 'anchor' && (
              <Button variant="primary" size="sm" onClick={handleComputePreview} disabled={enriching}>
                {enriching ? 'Loading EXIF...' : 'Preview'}
              </Button>
            )}
            {step === 'configure' && tier === 'anchor' && (
              <Button variant="primary" size="sm" onClick={() => setShowAnchorView(true)} disabled={enriching}>
                Place Anchors
              </Button>
            )}
            {step === 'preview' && (
              <Button variant="primary" size="sm" onClick={handleApply}>
                Apply Corrections
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(content, document.body)}
      {showAnchorView && (
        <PhotoAnchorView
          candidates={anchorCandidates.slice(0, 5)}
          photos={photos}
          onDone={handleAnchorsDone}
          onCancel={handleAnchorCancel}
        />
      )}
    </>
  );

  // ─── Step Renderers ────────────────────────────────

  function renderFlightStep() {
    if (flights.length === 0) {
      return (
        <div className="photo-reg-info">
          <strong>No photos found.</strong> Upload geo photos first using the buttons above.
        </div>
      );
    }

    return (
      <>
        <div className="photo-reg-info">
          Select a flight to register. Photos are grouped by capture date.
        </div>
        {flights.map((f) => (
          <div
            key={f.id}
            className={`photo-reg-flight-card ${selectedFlight?.id === f.id ? 'photo-reg-flight-card--selected' : ''}`}
            onClick={() => handleSelectFlight(f)}
          >
            <div>
              <div className="photo-reg-flight-card__label">
                {f.captureDate === 'unknown' ? 'Unknown Date' : f.captureDate}
              </div>
              <div className="photo-reg-flight-card__meta">
                {f.count} photo{f.count !== 1 ? 's' : ''}
              </div>
            </div>
            {f.gimbalDataCount > 0 ? (
              <span className="photo-reg-badge photo-reg-badge--green">
                Gimbal: {f.gimbalDataCount}/{f.count}
              </span>
            ) : (
              <span className="photo-reg-badge photo-reg-badge--gray">
                No gimbal data
              </span>
            )}
          </div>
        ))}
      </>
    );
  }

  function renderConfigureStep() {
    if (!selectedFlight) return null;

    const gimbalCount = photos.filter((p) => p.gimbalPitch != null && p.gimbalYaw != null).length;

    return (
      <>
        {/* Tier info */}
        {gimbalCount > 0 && (
          <div className="photo-reg-tier">
            <div className="photo-reg-tier__title">
              Automatic Correction Available
            </div>
            <div className="photo-reg-tier__desc">
              {gimbalCount}/{photos.length} photos have gimbal angle data.
              The offset will be computed per-photo from gimbal pitch, yaw, and altitude.
              {gimbalCount < photos.length && (
                <span> The remaining {photos.length - gimbalCount} will use average gimbal values.</span>
              )}
            </div>
            {tier !== 'gimbal' && (
              <Button variant="ghost" size="sm" onClick={() => setTier('gimbal')} style={{ marginTop: 8 }}>
                Use automatic mode
              </Button>
            )}
          </div>
        )}

        {/* Manual heading/tilt (Tier 2 or override) */}
        {(tier === 'manual' || gimbalCount === 0) && (
          <>
            <div className="photo-reg-info">
              <strong>Manual mode:</strong> Enter the drone camera heading and tilt angle.
              The system will shift all photos by the computed ground offset.
            </div>

            {/* Heading */}
            <div className="photo-reg-field">
              <div className="photo-reg-field__header">
                <span>
                  Camera Heading
                  {detectedHeading !== null && (
                    <span className="photo-reg-detected">
                      Detected: {Math.round(detectedHeading)}° ({compassLabel(detectedHeading)})
                    </span>
                  )}
                </span>
                <span className="photo-reg-field__value">{headingDeg}° ({compassLabel(headingDeg)})</span>
              </div>
              <input
                type="range" min={0} max={359} step={1}
                className="photo-reg-field__slider"
                value={headingDeg}
                onChange={(e) => setHeadingDeg(Number(e.target.value))}
              />
              <div className="photo-reg-heading-btns">
                {[
                  { label: 'N', deg: 0 },
                  { label: 'NE', deg: 45 },
                  { label: 'E', deg: 90 },
                  { label: 'SE', deg: 135 },
                  { label: 'S', deg: 180 },
                  { label: 'SW', deg: 225 },
                  { label: 'W', deg: 270 },
                  { label: 'NW', deg: 315 },
                ].map(({ label, deg }) => (
                  <button
                    key={deg}
                    className={`photo-reg-heading-btn ${headingDeg === deg ? 'photo-reg-heading-btn--active' : ''}`}
                    onClick={() => setHeadingDeg(deg)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tilt */}
            <div className="photo-reg-field">
              <div className="photo-reg-field__header">
                <span>Gimbal Tilt from Nadir</span>
                <span className="photo-reg-field__value">{tiltDeg}° → ~{estimatedOffset}m offset</span>
              </div>
              <input
                type="range" min={0} max={45} step={1}
                className="photo-reg-field__slider"
                value={tiltDeg}
                onChange={(e) => setTiltDeg(Number(e.target.value))}
              />
              <div className="photo-reg-field__range">
                <span>0° (straight down)</span>
                <span>45° (heavy tilt)</span>
              </div>
            </div>
          </>
        )}

        {/* Gimbal mode — show summary */}
        {tier === 'gimbal' && gimbalCount > 0 && (
          <>
            {/* Show gimbal stats */}
            {(() => {
              const gPhotos = photos.filter((p) => p.gimbalPitch != null && p.gimbalYaw != null);
              const avgPitch = gPhotos.reduce((s, p) => s + p.gimbalPitch!, 0) / gPhotos.length;
              const avgYaw = gPhotos.reduce((s, p) => s + p.gimbalYaw!, 0) / gPhotos.length;
              const avgAlt = gPhotos.reduce((s, p) => s + (p.altitude ?? 50), 0) / gPhotos.length;
              const tiltFromNadir = 90 + avgPitch;
              const avgOffset = avgAlt * Math.tan(Math.abs(tiltFromNadir) * Math.PI / 180);

              return (
                <div className="photo-reg-stats">
                  <div className="photo-reg-stat">
                    <div className="photo-reg-stat__value">{avgPitch.toFixed(1)}°</div>
                    <div className="photo-reg-stat__label">Avg Gimbal Pitch</div>
                  </div>
                  <div className="photo-reg-stat">
                    <div className="photo-reg-stat__value">{compassLabel(avgYaw)}</div>
                    <div className="photo-reg-stat__label">Avg Direction</div>
                  </div>
                  <div className="photo-reg-stat">
                    <div className="photo-reg-stat__value">~{avgOffset.toFixed(1)}m</div>
                    <div className="photo-reg-stat__label">Est. Offset</div>
                  </div>
                </div>
              );
            })()}
            <Button variant="ghost" size="sm" onClick={() => setTier('manual')}>
              Override with manual heading/tilt
            </Button>
          </>
        )}

        {/* Anchor mode (Tier 3) */}
        {tier === 'anchor' && (
          <>
            <div className="photo-reg-info">
              <strong>Anchor mode:</strong> Manually place 2-3 photos at their correct positions on the map.
              The system will compute a rigid transform and apply it to all photos.
            </div>
            <div className="photo-reg-info" style={{ fontSize: 12, opacity: 0.8 }}>
              Click "Place Anchors" below to open the split-screen view.
              Top {Math.min(5, anchorCandidates.length)} candidates selected for anchor placement.
            </div>
          </>
        )}

        {/* Switch to anchor mode */}
        {tier !== 'anchor' && gimbalCount === 0 && (
          <Button variant="ghost" size="sm" onClick={() => setTier('anchor')} style={{ marginTop: 8 }}>
            Use anchor placement instead
          </Button>
        )}
      </>
    );
  }

  function renderPreviewStep() {
    return (
      <>
        <div className="photo-reg-info">
          <strong>Preview:</strong> Orange dashed lines on the map show how each photo will be shifted.
          Verify the corrections look reasonable before applying.
        </div>

        <div className="photo-reg-stats">
          <div className="photo-reg-stat">
            <div className="photo-reg-stat__value">{stats.count}</div>
            <div className="photo-reg-stat__label">Photos</div>
          </div>
          <div className="photo-reg-stat">
            <div className="photo-reg-stat__value">{stats.avgOffset.toFixed(1)}m</div>
            <div className="photo-reg-stat__label">Avg Offset</div>
          </div>
          <div className="photo-reg-stat">
            <div className="photo-reg-stat__value">{compassLabel(stats.avgDirection)}</div>
            <div className="photo-reg-stat__label">Direction</div>
          </div>
        </div>

        <div className="photo-reg-info">
          Max offset: <strong>{stats.maxOffset.toFixed(1)}m</strong>.
          All corrections should point roughly <strong>{compassLabel(stats.avgDirection)}</strong>.
        </div>
      </>
    );
  }

  function renderDoneStep() {
    return (
      <div className="photo-reg-info" style={{ textAlign: 'center' }}>
        <strong>Registration complete!</strong><br />
        {stats.count} photos shifted by an average of {stats.avgOffset.toFixed(1)}m toward {compassLabel(stats.avgDirection)}.
        <br /><br />
        Trail lines will disappear shortly. You can save the project to persist changes.
      </div>
    );
  }
}
